"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";
import ServiceQuickNav from "@/components/ServiceQuickNav";

const m365Services = [
  { icon: "💻", title: "M365 Licensing & Setup", desc: "The right licences for your team, correctly configured from day one — no overspending, no gaps." },
  { icon: "📧", title: "Exchange Online & Business Email", desc: "Professional email, calendars, shared mailboxes, and contacts — reliable and professionally managed." },
  { icon: "💬", title: "Microsoft Teams & Collaboration", desc: "Teams setup, channels, meetings, file sharing, and calling for modern team communication." },
  { icon: "📁", title: "SharePoint & OneDrive", desc: "Document management, intranet, and secure file storage accessible from anywhere." },
  { icon: "🖥️", title: "Microsoft Intune", desc: "Cloud-based device and endpoint management for all your organisation’s devices." },
  { icon: "🔑", title: "Azure Active Directory", desc: "Identity management, SSO, MFA, and conditional access policies for your organisation." },
  { icon: "🔒", title: "Microsoft 365 Security & Compliance", desc: "Defender, DLP, compliance centre, and data protection built into your M365 environment." },
  { icon: "⚙️", title: "Ongoing M365 Administration", desc: "User management, licence optimisation, updates, and ongoing support for your M365 environment." },
];

const migrationSteps = [
  { num: "01", title: "Assessment", desc: "Evaluate your current environment, identify dependencies, and plan the migration scope." },
  { num: "02", title: "Planning", desc: "Detailed migration plan with timelines, rollback procedures, and communication strategy." },
  { num: "03", title: "Migration", desc: "Structured data migration with minimal disruption to your business operations." },
  { num: "04", title: "Optimisation", desc: "Post-migration tuning, user training, and ongoing management to maximise your investment." },
];

const workplaceBenefits = [
  { icon: "🌍", title: "Work from Anywhere", desc: "Access your files, email, and applications from any device, anywhere in the world." },
  { icon: "💰", title: "Reduced IT Costs", desc: "Move from on-premise servers to cloud — lower hardware costs and maintenance overhead." },
  { icon: "🔒", title: "Enterprise-Grade Security", desc: "Microsoft’s security infrastructure protecting your business data and communications." },
];

const m365Apps = [
  { name: "Teams", color: "#5B5EA6" },
  { name: "Outlook", color: "#0078D4" },
  { name: "SharePoint", color: "#038387" },
  { name: "OneDrive", color: "#0078D4" },
  { name: "Word", color: "#185ABD" },
  { name: "Excel", color: "#107C41" },
  { name: "PowerPoint", color: "#C43E1C" },
  { name: "Intune", color: "#0078D4" },
  { name: "Defender", color: "#00B4D8" },
];

export default function Microsoft365Page() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Cloud Solutions", href: "/cloud-solutions" },
            { label: "Microsoft 365" },
          ]}
        />

        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #0a0a2e 70%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(0,120,212,0.12) 0%, transparent 70%)" }} />
          <div className="absolute bottom-1/4 left-1/3 w-80 h-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(91,94,166,0.08) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(0,120,212,0.12)", border: "1px solid rgba(0,120,212,0.3)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#0078D4" }} />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Microsoft 365</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Microsoft 365 Solutions<br />
                <span style={{ background: "linear-gradient(135deg, #0078D4, #60A5FA)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>for Modern Workplaces</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg max-w-2xl mb-10" style={{ color: "#94A3B8" }}>
                Pro IT Solutions manages your complete Microsoft 365 environment — from licensing and setup to security, compliance, and ongoing administration.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/contact" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill" style={{ background: "linear-gradient(135deg, #0078D4, #60A5FA)", color: "white" }}>Get an M365 Quote</Link>
                <Link href="#m365-services" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-500 rounded-pill btn-secondary">View M365 Services</Link>
              </div>
            </FadeInView>
            <FadeInView delay={0.6}>
              <div className="flex flex-wrap justify-center gap-6 mt-12">
                {["Microsoft Partner", "M365 Certified", "Modern Workplace"]?.map((t) => (
                  <div key={t} className="flex items-center gap-2 text-[13px] font-body" style={{ color: "#64748B" }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#0078D4" }} />
                    {t}
                  </div>
                ))}
              </div>
            </FadeInView>
          </div>
        </section>

        {/* M365 Services */}
        <section id="m365-services" className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Microsoft 365 Services</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>Complete Microsoft 365 setup, migration, security, and ongoing management for your business.</p>
            </FadeInView>
            {/* Desktop grid */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch" staggerDelay={0.08}>
              {m365Services?.map((svc) => (
                <div key={svc?.title} className="rounded-card-lg p-6 transition-all duration-300 h-full flex flex-col" style={{ background: "rgba(0,120,212,0.05)", border: "1px solid rgba(0,120,212,0.15)" }}>
                  <div className="text-3xl mb-4">{svc?.icon}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{svc?.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{svc?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Modern Workplace Benefits */}
        <section className="py-20 lg:py-28 border-t" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Modern Workplace Benefits</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch" staggerDelay={0.1}>
              {workplaceBenefits?.map((b) => (
                <div key={b?.title} className="rounded-card-lg p-5 sm:p-8 h-full flex flex-col" style={{ background: "rgba(0,120,212,0.05)", border: "1px solid rgba(0,120,212,0.15)" }}>
                  <div className="text-3xl mb-4">{b?.icon}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{b?.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{b?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* M365 App Showcase */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-12">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>The Microsoft 365 App Suite</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>Everything your team needs to collaborate, communicate, and stay productive.</p>
            </FadeInView>
            <FadeInView>
              <div className="flex flex-wrap justify-center gap-4">
                {m365Apps?.map((app) => (
                  <div
                    key={app?.name}
                    className="flex flex-col items-center gap-2 px-5 py-4 rounded-2xl transition-all duration-300 hover:scale-105"
                    style={{ background: `${app?.color}15`, border: `1px solid ${app?.color}30`, minWidth: "80px" }}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center font-heading font-700 text-[13px]" style={{ background: `${app?.color}25`, color: app?.color }}>
                      {app?.name?.slice(0, 2)}
                    </div>
                    <span className="text-[12px] font-body" style={{ color: "#CBD5E1" }}>{app?.name}</span>
                  </div>
                ))}
              </div>
            </FadeInView>
          </div>
        </section>

        {/* Migration Steps */}
        <section className="py-20 lg:py-28 border-t" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>M365 Migration Process</h2>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-4 gap-6 items-stretch" staggerDelay={0.1}>
              {migrationSteps?.map((step) => (
                <div key={step?.num} className="rounded-card-lg p-6 text-center h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div className="font-heading font-800 text-[40px] mb-3" style={{ color: "rgba(0,120,212,0.35)" }}>{step?.num}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{step?.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{step?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Microsoft Partner Badge */}
        <section className="py-12 border-t border-b" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#070B14" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView>
              <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl" style={{ background: "rgba(0,120,212,0.12)", border: "1px solid rgba(0,120,212,0.3)" }}>📘</div>
                <div>
                  <div className="font-heading font-700 text-[20px] mb-1" style={{ color: "#F8FAFC" }}>Microsoft Partner — M365 Certified</div>
                  <p className="text-[14px] font-body" style={{ color: "#94A3B8" }}>Pro IT Solutions is a certified Microsoft Partner with deep expertise in Microsoft 365 deployment, security, and ongoing management.</p>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(0,120,212,0.08)", border: "1px solid rgba(0,120,212,0.25)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to get more from Microsoft 365?</h2>
                <p className="text-body-lg mb-8" style={{ color: "#94A3B8" }}>Let&apos;s plan your M365 migration and get your team working smarter in the cloud.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600" style={{ background: "linear-gradient(135deg, #0078D4, #60A5FA)", color: "white" }}>Book an M365 Assessment</Link>
                  <Link href="/cloud-solutions" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">View All Cloud Services</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <ServiceQuickNav currentPath="/cloud-solutions/microsoft-365" />

        <Footer />
      </main>
    </PageTransition>
  );
}
